import React from 'react';
import styled from 'styled-components';
import { Formik, Form, useField, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import HelpIcon from '@mui/icons-material/Help';

import { useCurrentAircraft } from 'ff/debrief/hooks';
import { useDebriefModal } from 'ff/debrief/modalManager';
import DebriefDialogTitle from 'ff/debrief/components/modals/DebriefDialogTitle';

interface VspeedFormValues {
    vR: number;
    vX: number;
    vY: number;
    vRef: number;
    vNe: number;
    vS0: number;
    vS1: number;
    vSW: number;
    vMxt: number;
}

type SubmitHandler = (
    values: VspeedFormValues,
    helpers: FormikHelpers<VspeedFormValues>
) => void;

const fieldSchema = Yup.number()
    .typeError('Must be number')
    .min(0, 'Must be greater than zero')
    .max(300, 'Must be less than 300 knots')
    .integer('Must be integer')
    .optional();

const formSchema = Yup.object().shape({
    vR: fieldSchema,
    vX: fieldSchema,
    vY: fieldSchema,
    vRef: fieldSchema,
    vNe: fieldSchema,
    vS0: fieldSchema,
    vS1: fieldSchema,
    vSW: fieldSchema,
    vMxt: fieldSchema,
});

const VspeedModal = () => {
    const { isOpen, close } = useDebriefModal('Vspeed');
    const { currentAircraft, vSpeedOverrides, standardSpeeds, setOverrides } =
        useCurrentAircraft();

    const handleSubmit: SubmitHandler = (values, helpers) => {
        const cleanedValues: VspeedFormValues = {
            vR: values.vR || null,
            vX: values.vX || null,
            vY: values.vY || null,
            vRef: values.vRef || null,
            vNe: values.vNe || null,
            vS0: values.vS0 || null,
            vS1: values.vS1 || null,
            vSW: values.vSW || null,
            vMxt: values.vMxt || null,
        };
        setOverrides(cleanedValues)
            .then(() => {
                helpers.setSubmitting(false);
                handleClose();
            })
            .catch(() => {
                helpers.setSubmitting(false);
                handleClose();
            });
    };

    const handleClose = () => {
        close();
    };

    const initialValues: VspeedFormValues = {
        vR: vSpeedOverrides?.vR || undefined,
        vX: vSpeedOverrides?.vX || undefined,
        vY: vSpeedOverrides?.vY || undefined,
        vRef: vSpeedOverrides?.vRef || undefined,
        vNe: vSpeedOverrides?.vNe || undefined,
        vS0: vSpeedOverrides?.vS0 || undefined,
        vS1: vSpeedOverrides?.vS1 || undefined,
        vSW: vSpeedOverrides?.vSW || undefined,
        vMxt: vSpeedOverrides?.vMxt || undefined,
    };

    if (!standardSpeeds) return null;
    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
            <Formik
                initialValues={initialValues}
                validationSchema={formSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting }) => (
                    <Form>
                        <DebriefDialogTitle onClose={handleClose}>
                            V-Speeds
                        </DebriefDialogTitle>
                        <DialogContent>
                            <AircraftInfo {...currentAircraft} />
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <HeaderCell></HeaderCell>
                                            <HeaderCell>Standard</HeaderCell>
                                            <HeaderCell>
                                                This Aircraft
                                            </HeaderCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <SpeedRow
                                            name="vR"
                                            label="Vr"
                                            standard={standardSpeeds.vR}
                                        />
                                        <SpeedRow
                                            name="vS0"
                                            label="Vs0"
                                            standard={standardSpeeds.vS0}
                                        />
                                        <SpeedRow
                                            name="vS1"
                                            label="Vs1"
                                            standard={standardSpeeds.vS1}
                                        />
                                        <SpeedRow
                                            name="vX"
                                            label="Vx"
                                            standard={standardSpeeds.vX}
                                        />
                                        <SpeedRow
                                            name="vY"
                                            label="Vy"
                                            standard={standardSpeeds.vY}
                                        />
                                        <SpeedRow
                                            name="vSW"
                                            label="Vsw"
                                            standard={standardSpeeds.vSW}
                                        />
                                        <SpeedRow
                                            name="vRef"
                                            label="Vref"
                                            standard={standardSpeeds.vRef}
                                        />
                                        <SpeedRow
                                            name="vNe"
                                            label="Vne"
                                            standard={standardSpeeds.vNe}
                                        />
                                        <SpeedRow
                                            name="vMxt"
                                            label="Vmxt"
                                            standard={standardSpeeds.vMxt}
                                        />
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                disabled={isSubmitting}
                                variant="outlined"
                                color="info"
                                size="small"
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={isSubmitting}
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="small"
                            >
                                Save
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

const SpeedRow = (props: {
    name: keyof VspeedFormValues;
    label: string;
    standard: number;
}) => {
    const [field, meta] = useField<number>(props.name);
    const tooltip = helpText[props.name];
    const value = field.value === undefined ? '' : field.value;
    return (
        <TableRow>
            <SpeedLabelCell>
                <Stack direction="row">
                    <span>{props.label}</span>
                    <HelpDescription tooltip={tooltip} />
                </Stack>
            </SpeedLabelCell>
            <StdCell>{props.standard}</StdCell>
            <EditCell>
                <TextField
                    size="small"
                    name={field.name}
                    value={value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={Boolean(meta.error)}
                    helperText={meta.error || undefined}
                />
            </EditCell>
        </TableRow>
    );
};

const AircraftInfo = (props: {
    tailNumber: string;
    manufacturer: string;
    aircraftModelName: string;
}) => {
    return (
        <Box mb={1}>
            <Stack>
                <Typography fontWeight="bold">
                    {props.tailNumber || 'Unknown Tail number'}
                </Typography>
                {props.manufacturer && props.aircraftModelName && (
                    <Typography>
                        {props.manufacturer} / {props.aircraftModelName}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

const SpeedLabelCell = styled(TableCell)`
    width: 100%;
    font-size: 14px;
    font-weight: 700;
`;

const StdCell = styled(TableCell)`
    font-size: 14px;
    text-align: end;
`;

const EditCell = styled(TableCell)`
    min-width: 160px;
    font-size: 14px;
    input {
        text-align: end;
    }
`;

const HeaderCell = styled(TableCell)`
    font-size: 14px;
    text-align: end;
    font-weight: 700;
`;

const helpText: { [speed in keyof VspeedFormValues]: string } = {
    vR: 'Rotation speed. The speed that the pilot begins rotating the aircraft prior to lift-off',
    vX: 'Best angle-of-climb speed. The airspeed at which an airplane gains the greatest amount of altitude in a given distance.',
    vY: 'Best rate-of-climb speed. This airspeed provides the most altitude gain in a given period of time.',
    vRef: 'Landing reference airspeed or threshold crossing airspeed',
    vNe: 'Never-exceed speed. Operating above this speed is prohibited since it may result in damage or structural failure.',
    vS0: 'Stalling speed or the minimum steady flight speed in the landing configuration.',
    vS1: 'Stalling speed or the minimum steady flight speed obtained in a specified configuration.',
    vSW: 'Recommended/typical touchdown speed',
    vMxt: 'Maximum taxi ground speed',
};

const HelpDescription = (props: { tooltip: string }) => {
    return (
        <Tooltip
            title={<Typography fontSize="14px">{props.tooltip}</Typography>}
            placement="top"
        >
            <HelpIcon
                sx={{
                    color: 'rgba(93,93,93,0.63)',
                    fontSize: '12px',
                    ml: 0.5,
                }}
            />
        </Tooltip>
    );
};

export default VspeedModal;
